import {
  add,
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  format,
  isThisMonth,
  isThisWeek,
  isThisYear,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from 'date-fns';

import { DateRangeType, RangeTypes } from '../../constants';
import { BpType, Direction, RangeType } from '../../enums';
import { IPhysiologicalData, IPhysiologicalMeasurement, IPhysiologicalMeasurements } from '../../interfaces';

export const getIntervalForRangeType = (rangeType: RangeType, currentDate: number) => {
  const getThisInterval = () => {
    const dateRange = DateRangeType[rangeType];
    const dateFrom = add(startOfDay(Date.now()), {
      days: dateRange.difference * -1,
    });
    const dateTo = endOfDay(Date.now());

    return { dateFrom, dateTo };
  };

  switch (rangeType) {
    case RangeType.DAY: {
      const dateFrom = startOfDay(currentDate);
      const dateTo = endOfDay(currentDate);

      return { dateFrom, dateTo };
    }
    case RangeType.WEEK: {
      if (isThisWeek(currentDate, { weekStartsOn: 1 })) {
        return getThisInterval();
      }

      const dateFrom = startOfWeek(currentDate, { weekStartsOn: 1 });
      const dateTo = endOfWeek(currentDate, { weekStartsOn: 1 });

      return { dateFrom, dateTo };
    }
    case RangeType.MONTH: {
      if (isThisMonth(currentDate)) {
        return getThisInterval();
      }

      const dateFrom = startOfMonth(currentDate);
      const dateTo = endOfMonth(currentDate);

      return { dateFrom, dateTo };
    }
    case RangeType.YEAR: {
      if (isThisYear(currentDate)) {
        return getThisInterval();
      }

      const dateFrom = startOfYear(currentDate);
      const dateTo = endOfYear(currentDate);

      return { dateFrom, dateTo };
    }
  }
};

export const getDateForRangeType = (selectedRangeType: RangeType, currentDate: number, currentRangeType: RangeType) => {
  const rangeType =
    RangeTypes.indexOf(selectedRangeType) > RangeTypes.indexOf(currentRangeType) ? selectedRangeType : currentRangeType;
  const { dateTo } = getIntervalForRangeType(rangeType, currentDate);
  return dateTo.getTime();
};

export const getDateForDirection = (direction: Direction, rangeType: RangeType, currentDate: number) => {
  const dateRange = DateRangeType[rangeType];
  const days = direction === Direction.BACKWARD ? dateRange.difference * -1 : dateRange.difference;
  return Math.min(add(currentDate, { days }).getTime(), endOfDay(Date.now()).getTime());
};

export const parsePhysiologicalData = (
  physiologicalData: IPhysiologicalData,
  rangeType: RangeType,
  patientCountryKey: string,
) => {
  if (rangeType !== RangeType.YEAR) {
    return physiologicalData;
  }

  const measurements = Object.values(physiologicalData.measurements).reduce(
    (acc, measurement: IPhysiologicalMeasurement) => {
      const key = +format(measurement._id, 'yyyyMM');
      const entry: IPhysiologicalMeasurement = acc[key];
      if (!entry) {
        return {
          ...acc,
          [key]: measurement,
        };
      }

      const avg = (first: number, second: number) => Math.round((first + second) / 2);
      const dia = avg(entry.dia, measurement.dia);
      const sys = avg(entry.sys, measurement.sys);
      const status = () => {
        if (patientCountryKey === 'us') {
          if (sys >= 180 || dia >= 120) return BpType.HYPERTENSIVE_CRISIS;
          if (sys >= 140 || dia >= 90) return BpType.STAGE_2;
          if (sys >= 130 || dia >= 80) return BpType.STAGE_1;
          if (sys >= 120) return BpType.ELEVATED;
          return BpType.NORMAL;
        }

        if (sys >= 180 || dia >= 110) return BpType.STAGE_3;
        if (sys >= 160 || dia >= 100) return BpType.STAGE_2;
        if (sys >= 140 || dia >= 90) return BpType.STAGE_1;
        if (sys >= 130 || dia >= 85) return BpType.ELEVATED;
        if (sys >= 120 || dia >= 80) return BpType.NORMAL;
        return BpType.OPTIMAL;
      };

      return {
        ...acc,
        [key]: {
          ...entry,
          bpType: status(),
          count: entry.count + measurement.count,
          dia,
          hr: avg(entry.hr, measurement.hr),
          sys,
          measurementTypes: [...entry.measurementTypes, ...measurement.measurementTypes],
        },
      };
    },
    {} as IPhysiologicalMeasurements,
  );

  return {
    ...physiologicalData,
    measurements,
  };
};
